import { Element } from "@xmpp/xml"
import { WORKFLOWS_NODE } from "./openclawWorkflowsParser"
import {
  FleetReminderEntry,
  FleetReminderWorkflow,
} from "@/reducers/openclawWorkflowsSlice"

/**
 * Parse all <fleet-reminder> children from a single PubSub item element.
 * Returns FleetReminderWorkflow[] so they can be stored in the same
 * openclawWorkflows slice as scheduled workflows.
 *
 * The synthetic id is `${itemId}:${name}` — fleet-reminder elements carry no
 * id attribute in the XML today.  The itemId is stored in `author` so the
 * slice's upsert/remove logic (which keys local items by author) works
 * correctly.
 */
export function parseFleetReminders(item: Element): FleetReminderWorkflow[] {
  const el = item.getChild("workflows", WORKFLOWS_NODE)
  if (!el) return []

  const itemId = item.getAttr("id") ?? ""

  return el.getChildren("fleet-reminder").flatMap((fr) => {
    const name = fr.getAttr("name")
    if (!name) return []

    const status = (fr.getAttr("status") ??
      "active") as FleetReminderWorkflow["status"]

    const advance = (fr.getAttr("advance") ?? "")
      .split(",")
      .map((s: string) => Number(s.trim()))
      .filter((n: number) => !Number.isNaN(n) && n > 0)

    const actionEl = fr.getChild("action")
    const action = actionEl
      ? {
          kind: actionEl.getAttr("kind") ?? "",
          message: actionEl.getChildText("message") ?? "",
        }
      : undefined

    const entries: FleetReminderEntry[] = fr
      .getChildren("entry")
      .flatMap((e) => {
        const plate = e.getAttr("plate")
        if (!plate) return []

        const entryStatus = (e.getAttr("status") ??
          "active") as FleetReminderEntry["status"]

        const dueEl = e.getChild("due")
        if (!dueEl) return []

        const dueKind = dueEl.getAttr("kind")
        let due: import("@/reducers/openclawWorkflowsSlice").FleetReminderDue
        if (dueKind === "yearly") {
          const month = Number(dueEl.getAttr("month"))
          const day = Number(dueEl.getAttr("day"))
          if (
            !Number.isInteger(month) ||
            month < 1 ||
            month > 12 ||
            !Number.isInteger(day) ||
            day < 1 ||
            day > 31
          )
            return []
          due = { kind: "yearly", month, day }
        } else if (dueKind === "date") {
          const date = dueEl.getAttr("date")
          if (!date || Number.isNaN(Date.parse(date))) return []
          due = { kind: "date", date }
        } else {
          return []
        }

        const firedEl = e.getChild("fired")
        const fired = firedEl
          ? {
              dueDate: firedEl.getAttr("dueDate") ?? "",
              advances: (firedEl.getAttr("advances") ?? "")
                .split(",")
                .map((s: string) => Number(s.trim()))
                .filter((n: number) => !Number.isNaN(n) && n > 0),
              acceptance: firedEl.getChild("acceptance")
                ? {
                    acceptedAt:
                      firedEl.getChild("acceptance")!.getAttr("acceptedAt") ??
                      "",
                  }
                : undefined,
            }
          : undefined

        return [{ plate, status: entryStatus, due, fired }]
      })

    return [
      {
        kind: "fleet-reminder",
        id: itemId,
        name,
        status,
        scope: "global",
        author: itemId,
        advance,
        action,
        entries,
      },
    ]
  })
}
